/**
 * dd-pricing-table.js
 *
 * Reflects Regios DOPP account-tier discounts on the /pages/pricing
 * tables. Mirrors the pattern in assets/dd-card2.js (related-product
 * cards) — there is no PDP context here, so we use the generic
 * `window.RegiosDOPP.api.v0.calculateDiscountedPrices({ productId,
 *  variantId, regularPriceInCents })` form.
 *
 * Architecture
 * ------------
 * Each <section data-dd-pricing-table> carries a probe variant id
 * (data-regios-variant-id, read in Liquid from
 *  product.metafields.custom.default_variant). We call Regios ONCE
 * per table, derive the discount percentage from the response, and
 * apply that single % to every wholesale cell in that table. Each
 * cell carries its own base-price-in-cents on data-base-cents, so
 * the cell is the source of truth for "what to discount" — the
 * probe variant is only used to ask Regios "what's this customer's
 * effective rate for this product?".
 *
 * Why one call per table instead of one per length:
 *  - The user case is account-wide tier discount (flat % per
 *    customer); discount % does not vary by variant. Calling per
 *    variant would still resolve to the same %, just at 8× the
 *    request cost.
 *  - dd-card2.js does it per variant because product cards each
 *    represent a distinct variant. A pricing-guide table is one
 *    product across many lengths — different problem shape.
 *
 * Failure modes
 * -------------
 * Any failure (Regios not installed, API not loaded within poll
 * timeout, customer has no discount, network/JS error) leaves the
 * server-rendered baseline visible. We never blank out the cell.
 *
 * Customer gate
 * -------------
 * Liquid only emits data-base-cents / data-regios-variant-id for
 * logged-in customers. Guest cells render the redacted login
 * affordance, which has no data-base-cents and is invisible to this
 * file. This script is a no-op on the guest view.
 */
(function () {
  'use strict';

  // Singleton guard — sections + asset hot-reload in theme editor can
  // re-execute this script. We want one watcher + one cache, period.
  if (window._ddPricingTableInit) return;
  window._ddPricingTableInit = true;

  /* ── Shared Regios DOPP v0 state ─────────────────────────────── */

  var regiosV0 = null;
  var regiosListening = false;
  var regiosSubscribers = [];
  // Cache keyed by `productId|variantId|baseCents` so reloads /
  // re-prices for the same probe hit the same memoized answer.
  var regiosCache = {};
  var regiosInFlight = {};

  function onRegiosReady(api) {
    regiosV0 = api;
    var subs = regiosSubscribers.slice();
    regiosSubscribers.length = 0;
    subs.forEach(function (fn) {
      try {
        fn(api);
      } catch (err) {
        // Don't let one subscriber's bug nuke the rest.
        console.error('[dd-pricing-table] subscriber error', err);
      }
    });
  }

  function whenRegiosReady(fn) {
    if (regiosV0) {
      fn(regiosV0);
      return;
    }
    regiosSubscribers.push(fn);
    startRegiosWatcher();
  }

  function startRegiosWatcher() {
    if (regiosListening) return;
    regiosListening = true;

    window.addEventListener('regios-dopp:api-initialized', function (e) {
      var v0 =
        (e && e.detail && e.detail.api) ||
        (window.RegiosDOPP &&
          window.RegiosDOPP.api &&
          window.RegiosDOPP.api.v0);
      if (v0 && !regiosV0) onRegiosReady(v0);
    });

    if (
      window.RegiosDOPP &&
      window.RegiosDOPP.api &&
      window.RegiosDOPP.api.v0
    ) {
      onRegiosReady(window.RegiosDOPP.api.v0);
      return;
    }

    // Poll fallback — some app embeds attach the global without
    // dispatching the init event. Mirrors dd-card2.js (every 500ms
    // for up to 30s, then give up silently).
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      var v0 =
        window.RegiosDOPP &&
        window.RegiosDOPP.api &&
        window.RegiosDOPP.api.v0;
      if (v0) {
        clearInterval(timer);
        if (!regiosV0) onRegiosReady(v0);
      } else if (attempts >= 60) {
        clearInterval(timer);
      }
    }, 500);
  }

  /* ── Money formatting ────────────────────────────────────────── */

  function formatMoney(cents) {
    var fmt = window.shopMoneyFormat
      ? String(window.shopMoneyFormat).replace(/<[^>]*>/g, '')
      : '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var out = fmt
      .replace(/\{\{\s*amount\s*\}\}/, withCommas)
      .replace(
        /\{\{\s*amount_no_decimals\s*\}\}/,
        Math.round(cents / 100).toString()
      )
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
    // Match Liquid's `money_without_trailing_zeros`: strip ".00" / ",00"
    // but keep non-zero decimals and trailing currency codes intact.
    return out.replace(/([.,])00(?!\d)/, '');
  }

  /* ── Regios probe (one call per table) ───────────────────────── */

  function probeKey(productId, variantId, baseCents) {
    return productId + '|' + variantId + '|' + baseCents;
  }

  function probeRegios(productId, variantId, baseCents) {
    var key = probeKey(productId, variantId, baseCents);
    if (regiosCache[key] !== undefined) {
      return Promise.resolve(regiosCache[key]);
    }
    if (regiosInFlight[key]) return regiosInFlight[key];
    if (!regiosV0 || typeof regiosV0.calculateDiscountedPrices !== 'function') {
      return Promise.resolve(null);
    }

    var p = regiosV0
      .calculateDiscountedPrices({
        productId: parseInt(productId, 10),
        variantId: parseInt(variantId, 10),
        regularPriceInCents: parseInt(baseCents, 10),
      })
      .then(function (result) {
        if (!result || !result.salePrice || !result.regularPrice) {
          regiosCache[key] = null;
          return null;
        }
        // Regios returns amounts in dollars (see docs/APP-INTEGRATIONS.md).
        var saleAmt = parseFloat(result.salePrice.amount);
        var regAmt = parseFloat(result.regularPrice.amount);
        if (!(saleAmt < regAmt) || !(regAmt > 0)) {
          regiosCache[key] = null;
          return null;
        }
        // Discount as a unit-fraction. We deliberately compute the
        // fraction from the API response rather than hard-coding a
        // tier % so the math stays correct regardless of how Regios
        // expresses the discount (flat %, fixed amount, etc.).
        var fraction = (regAmt - saleAmt) / regAmt;
        // Extract a plain-text discount name from Regios' HTML payload.
        // Used for the "$X with [Name]" affordance below the price.
        var name = '';
        var html =
          (result.description && result.description.html) ||
          result.discountMessage ||
          '';
        if (html) {
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          name = (tmp.textContent || tmp.innerText || '').trim();
        }
        var entry = { fraction: fraction, name: name };
        regiosCache[key] = entry;
        return entry;
      })
      .catch(function () {
        regiosCache[key] = null;
        return null;
      })
      .finally(function () {
        delete regiosInFlight[key];
      });

    regiosInFlight[key] = p;
    return p;
  }

  /* ── Tier callout reveal (page-level, one-shot) ──────────────── */

  // Becomes true once we've populated + revealed the callout for
  // this page load. Subsequent tables resolving the same discount
  // skip the reveal step. The callout is account-level (one
  // customer = one tier), so revealing once is correct.
  var tierCalloutRevealed = false;

  function revealTierCallout(name, fraction) {
    if (tierCalloutRevealed) return;
    if (!(fraction > 0)) return;

    var el = document.querySelector('[data-dd-tier-callout]');
    if (!el) return;

    var pct = Math.round(fraction * 100);
    // Edge case: round-down to 0 on micro-discounts. Show "<1%"
    // rather than the misleading "0%".
    var pctText = pct > 0 ? pct + '%' : '<1%';

    var nameEl = el.querySelector('[data-dd-tier-name]');
    var pctEl = el.querySelector('[data-dd-tier-discount]');

    if (nameEl) {
      // Fall back to a neutral label if Regios didn't surface a
      // human-readable tier name — better than rendering an empty
      // badge.
      nameEl.textContent = name && name.length ? name : 'Pro discount';
    }
    if (pctEl) {
      pctEl.textContent = pctText;
    }

    el.removeAttribute('hidden');
    tierCalloutRevealed = true;
  }

  /* ── Apply a discount fraction to every wholesale cell ───────── */

  function applyDiscountToTable(section, fraction, discountName) {
    if (!(fraction > 0)) return;
    var cells = section.querySelectorAll('[data-dd-wholesale-cell]');
    cells.forEach(function (cell) {
      var baseCents = parseInt(cell.getAttribute('data-base-cents'), 10);
      if (!isFinite(baseCents) || baseCents <= 0) return;
      var saleCents = Math.round(baseCents * (1 - fraction));
      if (saleCents <= 0 || saleCents >= baseCents) return;

      // Build the strike-original + sale span pair. Replaces the
      // server-rendered .dd-pricing-table__wholesale-render span so
      // we don't mutate other children (badges, etc.).
      var render = cell.querySelector('.dd-pricing-table__wholesale-render');
      if (!render) return;

      var originalEl = document.createElement('span');
      originalEl.className =
        'dd-pricing-table__price dd-pricing-table__price--original';
      originalEl.textContent = formatMoney(baseCents);
      // Hide the original from screen readers — assistive tech should
      // read the price the customer actually pays, not the strike.
      originalEl.setAttribute('aria-hidden', 'true');

      var saleEl = document.createElement('span');
      saleEl.className = 'dd-pricing-table__price dd-pricing-table__price--sale';
      saleEl.textContent = formatMoney(saleCents);

      // Replace the existing render in-place. Wrap in a flex container
      // so the two prices sit on the same row with consistent gap.
      var wrap = document.createElement('span');
      wrap.className = 'dd-pricing-table__price-pair';
      wrap.appendChild(originalEl);
      wrap.appendChild(saleEl);
      render.replaceWith(wrap);

      cell.classList.add('dd-pricing-table__price-cell--discounted');
    });

    if (discountName) {
      section.setAttribute('data-regios-discount-name', discountName);
    }
    section.classList.add('dd-pricing-table--discounted');
  }

  /* ── Init: find tables, probe, apply ─────────────────────────── */

  function initTable(section) {
    if (section._ddPricingInit) return;
    section._ddPricingInit = true;

    var productId = section.getAttribute('data-regios-product-id');
    var variantId = section.getAttribute('data-regios-variant-id');
    if (!productId || !variantId) return;

    // Pick the first wholesale cell's base price as the probe
    // regularPriceInCents. Regios uses this as the "what was the
    // price before any discount" anchor; using a real per-product
    // wholesale value gives us a more accurate response than using
    // the probe variant's own price (which may be a discount-anchor
    // variant on some setups).
    var probeCell = section.querySelector('[data-dd-wholesale-cell]');
    if (!probeCell) return;
    var probeBase = parseInt(probeCell.getAttribute('data-base-cents'), 10);
    if (!isFinite(probeBase) || probeBase <= 0) return;

    whenRegiosReady(function () {
      probeRegios(productId, variantId, probeBase).then(function (entry) {
        if (!entry || !(entry.fraction > 0)) return;
        applyDiscountToTable(section, entry.fraction, entry.name);
        // Page-level tier banner. revealTierCallout is idempotent —
        // the first table whose probe lands populates it, every
        // subsequent table is a no-op here.
        revealTierCallout(entry.name, entry.fraction);
      });
    });
  }

  function scanAndInit() {
    var sections = document.querySelectorAll('[data-dd-pricing-table]');
    sections.forEach(initTable);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanAndInit, { once: true });
  } else {
    scanAndInit();
  }
})();
